import { useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import { PracticeEditResidence } from 'practice/view-models/practice-edit-residence';
import { PmiButton } from 'shell/components/shared/button';
import { Fragment, memo, ReactElement, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import AccordionResidence from './accordion-residence';
import { PracticeAddress } from 'shell/models/practice-address';
import { DateTime } from 'luxon';
import { Message } from 'shell/components/shared/search-messages';
import * as yup from 'yup';
import ModalWarning from 'shell/components/shared/modal-warning';
import { PracticeDraftBEStep } from 'practice/constants/practice-management-step';

const validationSchema = () => {
  return yup.object({
    effectiveDate: yup
      .date()
      .typeError('Data non valida')
      .required('Campo richiesto')
      .nullable()
      .max(DateTime.now().plus({ day: 1 }), 'La data deve essere minore uguale ad oggi'),
  });
};

interface StepResidenceUnavailablityProps {
  onBack: () => void;
  onNext: () => void;
  idFamilyAnpr?: string;
  codeEnte?: string;
  yearId?: string;
  typeRegister?: string;
  progressive?: string;
  ForPracticeTemporary?: boolean;
  typePractice?: string;
  maxActiveStep: number;
  disabledAll?: boolean;
  iniziativa?: string;
  intestatarioIsAnpr?: boolean;
  fromPerson?: string;
}

const StepResidenceUnavailablityComponent = ({
  onBack,
  onNext,
  idFamilyAnpr,
  codeEnte,
  ForPracticeTemporary,
  yearId,
  typeRegister,
  progressive,
  typePractice,
  maxActiveStep,
  disabledAll,
  iniziativa,
  intestatarioIsAnpr,
  fromPerson,
}: StepResidenceUnavailablityProps): ReactElement => {
  const [warning, setWarning] = useState<{
    view: boolean;
    warning: Array<Message>;
    values: PracticeEditResidence;
  }>({
    view: false,
    warning: [],
    values: {},
  });

  const [indirizzo] = useHttpFetch<HttpResponse<PracticeAddress>, PracticeAddress>(
    `${__PRACTICE_API_URL__}/pratiche/indirizzi/italiano-o-estero/${codeEnte}-${yearId}-${typeRegister}-${progressive}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: (response) => {
        return response.data.payload;
      },
    }
  );

  const updateData = useHttpClient<HttpResponse<PracticeAddress>>(
    `${__PRACTICE_API_URL__}/pratiche/registrazione-nuovo-indirizzo`,
    {
      options: { method: 'POST' },
    }
  );

  const onSave = useEventCallback((values: PracticeEditResidence) => {
    if (disabledAll) {
      onNext();
      return;
    }

    const data = {
      pratica: {
        key: {
          codiceEnte: codeEnte || undefined,
          tipoRegistro: typeRegister || undefined,
          idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
          progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
        },
      },
      casoPratica: '0',
      dataDecorrenza: values.effectiveDate
        ? values.effectiveDate.toFormat('yyyy-MM-dd')
        : undefined,
      stepBozza:
        maxActiveStep > PracticeDraftBEStep.Residence
          ? maxActiveStep
          : PracticeDraftBEStep.Residence,
    };

    (updateData({ data }) as ResponsePromise<HttpResponse<PracticeAddress>>).then((response) => {
      const castedResponse = response?.data.payload;

      if ((castedResponse?.warning || []).length > 0) {
        const warningResult = (castedResponse?.warning || []).map((item, index) => {
          return {
            id: index,
            value: item || '',
          };
        });

        setWarning({ view: true, warning: warningResult, values });

        return;
      }

      toast.success('Operazione eseguita con successo');

      onNext();
    });
  });

  const initialValues: PracticeEditResidence = useMemo(() => {
    if (indirizzo.isLoading || indirizzo.hasErrors || indirizzo.pristine || !indirizzo.data) {
      return {
        isFromData: false,
      };
    }

    const data: PracticeAddress = (indirizzo.data as PracticeAddress) || {};

    return {
      effectiveDate: data.dataDecorrenza ? DateTime.fromISO(data.dataDecorrenza || '') : undefined,
    } as PracticeEditResidence;
  }, [indirizzo]);

  const onCloseWarning = useEventCallback(() => {
    setWarning({ view: false, warning: [], values: {} });

    onNext();
  });

  const isLoading = indirizzo.isLoading || indirizzo.hasErrors || indirizzo.pristine;

  return (
    <Fragment>
      {isLoading ? undefined : (
        <Formik
          initialValues={initialValues}
          onSubmit={disabledAll ? onNext : onSave}
          validationSchema={validationSchema()}
        >
          <Form>
            <AccordionResidence
              ForPracticeTemporary={ForPracticeTemporary}
              idFamilyAnpr={idFamilyAnpr}
              codeEnte={codeEnte}
              yearId={yearId}
              typeRegister={typeRegister}
              intestatarioIsAnpr={intestatarioIsAnpr}
              progressive={progressive}
              typePractice={typePractice}
              fromPerson={fromPerson}
              disabled={disabledAll}
              iniziativa={iniziativa}
            />
            <ModalWarning
              open={warning.view as boolean}
              onClose={onCloseWarning}
              message={(warning.warning as Array<Message>)?.map((el) => el.value)?.join('')}
              withAnnulment
              onAnnulment={() => setWarning({ view: false, warning: [], values: {} })}
              nameButton="Prosegui"
            />
            <div className="pmi-pt-3 pmi-d-flex pmi-justify-content-center">
              <div className="pmi-w-25 pmi-w-lg-15">
                <PmiButton variant="outlined" fullWidth color="cdmBrand" onClick={onBack}>
                  Indietro
                </PmiButton>
              </div>
              <div className="pmi-pl-2 pmi-w-25 pmi-w-lg-15">
                <PmiButton variant="outlined" fullWidth color="cdmBrandThird" type="submit">
                  Avanti
                </PmiButton>
              </div>
            </div>
          </Form>
        </Formik>
      )}
    </Fragment>
  );
};

StepResidenceUnavailablityComponent.displayName = 'step component Residence unavailability';

const StepResidenceUnavailablity = memo(
  StepResidenceUnavailablityComponent
) as typeof StepResidenceUnavailablityComponent;

export default StepResidenceUnavailablity;
